/* eslint-disable no-console */
import { useMemo, useCallback } from "react";

export function useGestionCalcs({
  pais,
  selectedYear,
  monthsToShow,
  currentYear,
  modoCombinado,
  negociosCarteleria,
  negociosCarteleriaChile,
  negociosCarteleriaPeru,
  clientes,
  edificios,
  costosGestionData,
  costosPublicidadData,
  convertirAMonedaLocal,
  getCostoGestionValue,
  getDolarValue,
  dolarMensualPeru,
  dolarMensualChile,
  dolarFallback,
  modalRefresh,
}) {
  // ─── Helper: meses_facturacion → array[12] de "cuántas cuotas gestion/contenidos cayeron en cada mes" ───
  // Para cada entry de tipo 'gestion'/'contenidos' cuya facturafecha cae en `year`,
  // suma 1 cuota al mes de facturafecha. Retorna null si el negocio no tiene meses_facturacion populated.
  // El caller multiplica por unitario × pantallas para obtener el monto.
  //
  // Regla: una entry `tipo: "gestion"` representa "se facturó el mes" para ese negocio.
  // Si el negocio tiene `incluye_contenidos = true`, contenidos viaja en la misma factura SII
  // que gestión (no requiere entry separada). Por eso, en los meses donde se facturó gestión
  // y no hay entry explícita de contenidos, se asume que contenidos también se facturó.
  const getCuotasFacturadasPorMes = (negocio, year) => {
    const entries = Array.isArray(negocio.meses_facturacion) ? negocio.meses_facturacion : [];
    if (entries.length === 0) return null;
    const incluyeContenidos = !!negocio.incluye_contenidos;
    const result = Array.from({ length: 12 }, () => ({ gestion: 0, contenidos: 0, fechaFactura: null }));
    let hasAny = false;
    entries.forEach((e) => {
      if (!e || !e.facturafecha) return;
      if (e.tipo !== "gestion" && e.tipo !== "contenidos") return;
      const [fy, fm] = e.facturafecha.split("-").map(Number);
      if (fy !== year) return;
      const idx = fm - 1;
      if (idx < 0 || idx > 11) return;
      result[idx][e.tipo] += 1;
      result[idx].fechaFactura = e.facturafecha;
      hasAny = true;
    });
    // Contenidos implícito: rellenar solo los meses con gestión donde NO hubo entry explícita
    // de contenidos. Si ya hay entry explícita, respetarla y no duplicar.
    // Se aplica solo si el mes del slot cae dentro del rango [contenidos_fecha_inicio, contenidos_fecha_fin]
    // del negocio. Si alguno de los dos extremos es null, ese extremo no restringe.
    if (incluyeContenidos) {
      const contInicio = negocio.contenidos_fecha_inicio
        ? new Date(negocio.contenidos_fecha_inicio + "T00:00:00")
        : null;
      const contFin = negocio.contenidos_fecha_fin ? new Date(negocio.contenidos_fecha_fin + "T00:00:00") : null;
      result.forEach((slot, idx) => {
        if (slot.gestion > 0 && slot.contenidos === 0) {
          const slotDate = new Date(year, idx, 1);
          const enRango = (!contInicio || slotDate >= contInicio) && (!contFin || slotDate <= contFin);
          if (enRango) {
            slot.contenidos = slot.gestion;
          }
        }
      });
    }
    return hasAny ? result : null;
  };

  // ─── 1. detalleGestionContenidosPorMes ───
  const detalleGestionContenidosPorMes = useMemo(() => {
    const detalle = Array(12)
      .fill()
      .map(() => []);
    negociosCarteleria.forEach((negocio) => {
      if (negocio.eliminada || negocio.estado !== "Activo" || !negocio.fecha_inicio) return;
      const fechaInicio = new Date(negocio.fecha_inicio);
      const plazoMeses = parseInt(negocio.plazo_contrato_meses) || 0;
      if (plazoMeses === 0) return;
      const fechaFin = new Date(fechaInicio);
      fechaFin.setMonth(fechaInicio.getMonth() + plazoMeses);
      fechaFin.setDate(fechaFin.getDate() - 1);
      const pantallas = parseInt(negocio.cantidad_pantallas) || 1;
      const gestionUnitario = parseFloat(negocio.fee_gestion_unitario) || 0;
      const contenidosUnitario = negocio.incluye_contenidos ? parseFloat(negocio.fee_contenidos_unitario) || 0 : 0;
      let nombreReferencia = `ID ${negocio.referencia_id}`;
      if (negocio.tipo_vertical === "TD" && clientes[negocio.referencia_id]) {
        nombreReferencia = clientes[negocio.referencia_id].nombre;
      } else if (negocio.tipo_vertical === "EO" && edificios[negocio.referencia_id]) {
        nombreReferencia = edificios[negocio.referencia_id]?.edificio;
      }
      // ── Modo "facturado real": mostrar el line item solo en los meses donde hay factura ──
      const cuotasFacturadas = getCuotasFacturadasPorMes(negocio, selectedYear);
      if (cuotasFacturadas) {
        cuotasFacturadas.forEach((slot, m) => {
          if (slot.gestion === 0 && slot.contenidos === 0) return;
          const gestionLocal = convertirAMonedaLocal(
            gestionUnitario * pantallas * slot.gestion,
            negocio.moneda_gestion,
            pais,
            negocio.id,
            m + 1,
            selectedYear,
            slot.fechaFactura
          );
          const contenidosLocal = convertirAMonedaLocal(
            contenidosUnitario * pantallas * slot.contenidos,
            negocio.moneda_contenidos,
            pais,
            negocio.id,
            m + 1,
            selectedYear,
            slot.fechaFactura
          );
          detalle[m].push({
            id: negocio.id,
            tipo: negocio.tipo_vertical,
            nombre: nombreReferencia,
            pantallas,
            cuotasFacturadas: Math.max(slot.gestion, slot.contenidos, 1),
            facturaFecha: slot.fechaFactura,
            gestionUnitario,
            gestionMoneda: negocio.moneda_gestion,
            contenidosUnitario,
            contenidosMoneda: negocio.moneda_contenidos,
            totalLocal: gestionLocal + contenidosLocal,
          });
        });
        return;
      }
      for (let m = 0; m < 12; m++) {
        const ultimoDiaMes = new Date(selectedYear, m + 1, 0);
        if (fechaInicio <= ultimoDiaMes) {
          const gestionLocal = convertirAMonedaLocal(
            gestionUnitario * pantallas,
            negocio.moneda_gestion,
            pais,
            negocio.id,
            m + 1,
            selectedYear
          );
          const contenidosLocal = convertirAMonedaLocal(
            contenidosUnitario * pantallas,
            negocio.moneda_contenidos,
            pais,
            negocio.id,
            m + 1,
            selectedYear
          );
          detalle[m].push({
            id: negocio.id,
            tipo: negocio.tipo_vertical,
            nombre: nombreReferencia,
            pantallas,
            gestionUnitario,
            gestionMoneda: negocio.moneda_gestion,
            contenidosUnitario,
            contenidosMoneda: negocio.moneda_contenidos,
            totalLocal: gestionLocal + contenidosLocal,
          });
        }
      }
    });
    return detalle.map((mes) =>
      mes.sort((a, b) => {
        if (a.tipo !== b.tipo) return a.tipo === "EO" ? -1 : 1;
        return a.nombre.localeCompare(b.nombre);
      })
    );
  }, [
    negociosCarteleria,
    clientes,
    edificios,
    currentYear,
    pais,
    convertirAMonedaLocal,
    costosGestionData,
    modalRefresh,
  ]);

  // ─── 2. calcularCostoSoftwareEstimado ───
  const calcularCostoSoftwareEstimado = useCallback(
    (monthIndex) => {
      const pantallasActivas = detalleGestionContenidosPorMes[monthIndex].reduce(
        (sum, item) => sum + item.pantallas,
        0
      );
      if (pantallasActivas === 0) return 0;
      const fechaDia1 = `${currentYear}-${String(monthIndex + 1).padStart(2, "0")}-01`;
      const dolar = getDolarValue(fechaDia1);
      const costoUSD = pantallasActivas * 10.99;
      return Math.round(costoUSD * dolar);
    },
    [detalleGestionContenidosPorMes, currentYear, getDolarValue]
  );

  // ─── 3. getFechaFacturaHardware ───
  const getFechaFacturaHardware = useCallback(
    (negocio, mesIndex) => {
      const registro = costosGestionData.find(
        (c) =>
          Number(c.negocio_id) === Number(negocio.id) &&
          Number(c.mes) === mesIndex + 1 &&
          Number(c.ano) === currentYear &&
          c.fecha_factura
      );
      if (registro?.fecha_factura) return registro.fecha_factura.split("T")[0];
      const ultimoDia = new Date(currentYear, mesIndex + 1, 0);
      return ultimoDia.toISOString().split("T")[0];
    },
    [costosGestionData, currentYear]
  );

  // ─── 4. getSoftwareDSValueA ───
  const getSoftwareDSValueA = useCallback(
    (monthIndex) => {
      const row = costosPublicidadData[monthIndex];
      const valorTotalFactura = row ? Number(row.software_ds) || 0 : 0;
      const costoCarteleria = calcularCostoSoftwareEstimado(monthIndex);
      return Math.max(0, valorTotalFactura - costoCarteleria);
    },
    [costosPublicidadData, calcularCostoSoftwareEstimado]
  );

  // ─── 5. ingresosGestionContenidosMensual (current country) ───
  const ingresosGestionContenidosMensual = useMemo(() => {
    const mapa = Array(12).fill(0);
    negociosCarteleria.forEach((negocio) => {
      if (negocio.eliminada || negocio.estado !== "Activo" || !negocio.fecha_inicio) return;
      const fechaInicio = new Date(negocio.fecha_inicio);
      const plazoMeses = parseInt(negocio.plazo_contrato_meses) || 0;
      if (plazoMeses === 0) return;
      const fechaFin = new Date(fechaInicio);
      fechaFin.setMonth(fechaInicio.getMonth() + plazoMeses);
      fechaFin.setDate(fechaFin.getDate() - 1);
      const pantallas = parseInt(negocio.cantidad_pantallas) || 1;
      const gestionUnitario = parseFloat(negocio.fee_gestion_unitario) || 0;
      const contenidosUnitario = negocio.incluye_contenidos ? parseFloat(negocio.fee_contenidos_unitario) || 0 : 0;
      // ── Modo "facturado real": si meses_facturacion tiene entries, asignar al mes de facturafecha ──
      const cuotasFacturadas = getCuotasFacturadasPorMes(negocio, selectedYear);
      if (cuotasFacturadas) {
        cuotasFacturadas.forEach((slot, m) => {
          if (slot.gestion === 0 && slot.contenidos === 0) return;
          const fechaParaUF = slot.fechaFactura;
          let ingresoLocal = 0;
          if (slot.gestion > 0 && gestionUnitario > 0) {
            ingresoLocal += convertirAMonedaLocal(
              gestionUnitario * pantallas * slot.gestion,
              negocio.moneda_gestion,
              pais,
              negocio.id,
              m + 1,
              selectedYear,
              fechaParaUF
            );
          }
          if (slot.contenidos > 0 && contenidosUnitario > 0) {
            ingresoLocal += convertirAMonedaLocal(
              contenidosUnitario * pantallas * slot.contenidos,
              negocio.moneda_contenidos,
              pais,
              negocio.id,
              m + 1,
              selectedYear,
              fechaParaUF
            );
          }
          mapa[m] += ingresoLocal;
        });
        return;
      }
      const getFechaFactura = (mesIndex) => {
        const registro = costosGestionData.find(
          (c) =>
            Number(c.negocio_id) === negocio.id &&
            Number(c.mes) === mesIndex + 1 &&
            Number(c.ano) === currentYear &&
            c.fecha_factura
        );
        return registro?.fecha_factura?.split("T")[0] || null;
      };
      for (let m = 0; m < 12; m++) {
        const ultimoDiaMes = new Date(selectedYear, m + 1, 0);
        if (fechaInicio <= ultimoDiaMes) {
          const fechaFactura = getFechaFactura(m);
          const fechaParaUF = fechaFactura || ultimoDiaMes.toISOString().split("T")[0];
          let ingresoLocal = 0;
          if (gestionUnitario > 0) {
            ingresoLocal += convertirAMonedaLocal(
              gestionUnitario * pantallas,
              negocio.moneda_gestion,
              pais,
              negocio.id,
              m + 1,
              selectedYear,
              fechaParaUF
            );
          }
          if (contenidosUnitario > 0) {
            ingresoLocal += convertirAMonedaLocal(
              contenidosUnitario * pantallas,
              negocio.moneda_contenidos,
              pais,
              negocio.id,
              m + 1,
              selectedYear,
              fechaParaUF
            );
          }
          mapa[m] += ingresoLocal;
        }
      }
    });
    return mapa;
  }, [negociosCarteleria, currentYear, pais, convertirAMonedaLocal, costosGestionData]);

  // ─── 6. ingresosGestionContenidosMensualAnoAnterior ───
  const añoAnteriorGestion = selectedYear - 1;
  const ingresosGestionContenidosMensualAnoAnterior = useMemo(() => {
    const mapa = Array(12).fill(0);
    negociosCarteleria.forEach((negocio) => {
      if (negocio.eliminada || negocio.estado !== "Activo" || !negocio.fecha_inicio) return;
      const fechaInicio = new Date(negocio.fecha_inicio);
      const plazoMeses = parseInt(negocio.plazo_contrato_meses) || 0;
      if (plazoMeses === 0) return;
      const fechaFin = new Date(fechaInicio);
      fechaFin.setMonth(fechaInicio.getMonth() + plazoMeses);
      fechaFin.setDate(fechaFin.getDate() - 1);
      const pantallas = parseInt(negocio.cantidad_pantallas) || 1;
      const gestionUnitario = parseFloat(negocio.fee_gestion_unitario) || 0;
      const contenidosUnitario = negocio.incluye_contenidos ? parseFloat(negocio.fee_contenidos_unitario) || 0 : 0;
      // ── Modo "facturado real" ──
      const cuotasFacturadas = getCuotasFacturadasPorMes(negocio, añoAnteriorGestion);
      if (cuotasFacturadas) {
        cuotasFacturadas.forEach((slot, m) => {
          if (slot.gestion === 0 && slot.contenidos === 0) return;
          let ingresoLocal = 0;
          if (slot.gestion > 0 && gestionUnitario > 0) {
            ingresoLocal += convertirAMonedaLocal(
              gestionUnitario * pantallas * slot.gestion,
              negocio.moneda_gestion,
              pais,
              negocio.id,
              m + 1,
              añoAnteriorGestion,
              slot.fechaFactura
            );
          }
          if (slot.contenidos > 0 && contenidosUnitario > 0) {
            ingresoLocal += convertirAMonedaLocal(
              contenidosUnitario * pantallas * slot.contenidos,
              negocio.moneda_contenidos,
              pais,
              negocio.id,
              m + 1,
              añoAnteriorGestion,
              slot.fechaFactura
            );
          }
          mapa[m] += ingresoLocal;
        });
        return;
      }
      for (let m = 0; m < 12; m++) {
        const ultimoDiaMes = new Date(añoAnteriorGestion, m + 1, 0);
        if (fechaInicio <= ultimoDiaMes) {
          const fechaParaUF = ultimoDiaMes.toISOString().split("T")[0];
          let ingresoLocal = 0;
          if (gestionUnitario > 0) {
            ingresoLocal += convertirAMonedaLocal(
              gestionUnitario * pantallas,
              negocio.moneda_gestion,
              pais,
              negocio.id,
              m + 1,
              añoAnteriorGestion,
              fechaParaUF
            );
          }
          if (contenidosUnitario > 0) {
            ingresoLocal += convertirAMonedaLocal(
              contenidosUnitario * pantallas,
              negocio.moneda_contenidos,
              pais,
              negocio.id,
              m + 1,
              añoAnteriorGestion,
              fechaParaUF
            );
          }
          mapa[m] += ingresoLocal;
        }
      }
    });
    return mapa;
  }, [negociosCarteleria, añoAnteriorGestion, pais, convertirAMonedaLocal]);

  // ─── 7. ingresosGestionContenidosMensualChile ───
  const ingresosGestionContenidosMensualChile = useMemo(() => {
    const mapa = Array(12).fill(0);
    negociosCarteleriaChile.forEach((negocio) => {
      if (negocio.eliminada || negocio.estado !== "Activo" || !negocio.fecha_inicio) return;
      const fechaInicio = new Date(negocio.fecha_inicio);
      const plazoMeses = parseInt(negocio.plazo_contrato_meses) || 0;
      if (plazoMeses === 0) return;
      const fechaFin = new Date(fechaInicio);
      fechaFin.setMonth(fechaInicio.getMonth() + plazoMeses);
      fechaFin.setDate(fechaFin.getDate() - 1);
      const pantallas = parseInt(negocio.cantidad_pantallas) || 1;
      const gestionUnitario = parseFloat(negocio.fee_gestion_unitario) || 0;
      const contenidosUnitario = negocio.incluye_contenidos ? parseFloat(negocio.fee_contenidos_unitario) || 0 : 0;
      // ── Modo "facturado real" ──
      const cuotasFacturadas = getCuotasFacturadasPorMes(negocio, selectedYear);
      if (cuotasFacturadas) {
        cuotasFacturadas.forEach((slot, m) => {
          if (slot.gestion === 0 && slot.contenidos === 0) return;
          let ingresoLocal = 0;
          if (slot.gestion > 0 && gestionUnitario > 0) {
            ingresoLocal += convertirAMonedaLocal(
              gestionUnitario * pantallas * slot.gestion,
              negocio.moneda_gestion,
              "Chile",
              negocio.id,
              m + 1,
              selectedYear,
              slot.fechaFactura
            );
          }
          if (slot.contenidos > 0 && contenidosUnitario > 0) {
            ingresoLocal += convertirAMonedaLocal(
              contenidosUnitario * pantallas * slot.contenidos,
              negocio.moneda_contenidos,
              "Chile",
              negocio.id,
              m + 1,
              selectedYear,
              slot.fechaFactura
            );
          }
          mapa[m] += ingresoLocal;
        });
        return;
      }
      for (let m = 0; m < 12; m++) {
        const ultimoDiaMes = new Date(selectedYear, m + 1, 0);
        if (fechaInicio <= ultimoDiaMes) {
          const fechaParaUF = ultimoDiaMes.toISOString().split("T")[0];
          let ingresoLocal = 0;
          if (gestionUnitario > 0) {
            ingresoLocal += convertirAMonedaLocal(
              gestionUnitario * pantallas,
              negocio.moneda_gestion,
              "Chile",
              negocio.id,
              m + 1,
              selectedYear,
              fechaParaUF
            );
          }
          if (contenidosUnitario > 0) {
            ingresoLocal += convertirAMonedaLocal(
              contenidosUnitario * pantallas,
              negocio.moneda_contenidos,
              "Chile",
              negocio.id,
              m + 1,
              selectedYear,
              fechaParaUF
            );
          }
          mapa[m] += ingresoLocal;
        }
      }
    });
    return mapa;
  }, [negociosCarteleriaChile, selectedYear, convertirAMonedaLocal]);

  // ─── 8. ingresosGestionContenidosMensualPeru ───
  const ingresosGestionContenidosMensualPeru = useMemo(() => {
    const mapa = Array(12).fill(0);
    // Conversión a PEN bound a Perú. No se reutiliza convertirAMonedaLocal de ER/index.js porque
    // esa función usa getDolarValue del país del usuario, no del negocio.
    const toPEN = (monto, moneda, mes) => {
      if (!monto) return 0;
      const m = (moneda || "PEN").toUpperCase();
      if (m === "USD" || m === "US$") return monto * (dolarMensualPeru[mes] || dolarFallback["Perú"]);
      // PEN directo. UF/CLP no deberían aparecer en negocios Perú (data entry inválido);
      // se tratan como PEN defensivamente para no inflar valores como en el bug UF+CLP de Chile.
      return monto;
    };
    negociosCarteleriaPeru.forEach((negocio) => {
      if (negocio.eliminada || negocio.estado !== "Activo" || !negocio.fecha_inicio) return;
      const fechaInicio = new Date(negocio.fecha_inicio);
      const plazoMeses = parseInt(negocio.plazo_contrato_meses) || 0;
      if (plazoMeses === 0) return;
      const fechaFin = new Date(fechaInicio);
      fechaFin.setMonth(fechaInicio.getMonth() + plazoMeses);
      fechaFin.setDate(fechaFin.getDate() - 1);
      const pantallas = parseInt(negocio.cantidad_pantallas) || 1;
      const gestionUnitario = parseFloat(negocio.fee_gestion_unitario) || 0;
      const contenidosUnitario = negocio.incluye_contenidos ? parseFloat(negocio.fee_contenidos_unitario) || 0 : 0;
      // ── Modo "facturado real": si meses_facturacion tiene entries, asignar al mes de facturafecha ──
      const cuotasFacturadas = getCuotasFacturadasPorMes(negocio, selectedYear);
      if (cuotasFacturadas) {
        cuotasFacturadas.forEach((slot, m) => {
          if (slot.gestion === 0 && slot.contenidos === 0) return;
          let ingresoLocal = 0;
          if (slot.gestion > 0 && gestionUnitario > 0) {
            ingresoLocal += toPEN(gestionUnitario * pantallas * slot.gestion, negocio.moneda_gestion, m);
          }
          if (slot.contenidos > 0 && contenidosUnitario > 0) {
            ingresoLocal += toPEN(
              contenidosUnitario * pantallas * slot.contenidos,
              negocio.moneda_contenidos || negocio.moneda_gestion,
              m
            );
          }
          mapa[m] += ingresoLocal;
        });
        return;
      }
      for (let m = 0; m < 12; m++) {
        const ultimoDiaMes = new Date(selectedYear, m + 1, 0);
        if (fechaInicio <= ultimoDiaMes) {
          let ingresoLocal = 0;
          if (gestionUnitario > 0) {
            ingresoLocal += toPEN(gestionUnitario * pantallas, negocio.moneda_gestion, m);
          }
          if (contenidosUnitario > 0) {
            ingresoLocal += toPEN(
              contenidosUnitario * pantallas,
              negocio.moneda_contenidos || negocio.moneda_gestion,
              m
            );
          }
          mapa[m] += ingresoLocal;
        }
      }
    });
    return mapa;
  }, [negociosCarteleriaPeru, selectedYear, dolarMensualPeru, dolarFallback]);

  // ─── 9. ytdSoftwareDS, ytdOtros ───
  const ytdSoftwareDS = useMemo(() => {
    let total = 0;
    for (let i = 0; i < monthsToShow; i++) {
      const valor = getCostoGestionValue(i, "software_ds") || 0;
      total += valor > 0 ? valor : calcularCostoSoftwareEstimado(i);
    }
    return total;
  }, [monthsToShow, calcularCostoSoftwareEstimado, costosGestionData]);

  const ytdOtros = costosGestionData.slice(0, monthsToShow).reduce((a, row) => a + (Number(row?.otros) || 0), 0);

  // ─── 10. margenGestionContenidosMensual ───
  const margenGestionContenidosMensual = useMemo(
    () =>
      Array(12)
        .fill(0)
        .map((_, i) => {
          if (i >= monthsToShow) return 0;
          const ingresos = ingresosGestionContenidosMensual[i] || 0;
          const softwareSupabase = Number(getCostoGestionValue(i, "software_ds")) || 0;
          const softwareEstimado = calcularCostoSoftwareEstimado(i);
          const softwareUsado = softwareSupabase > 0 ? softwareSupabase : softwareEstimado;
          const otros = Number(getCostoGestionValue(i, "otros")) || 0;
          return ingresos - softwareUsado - otros;
        }),
    [ingresosGestionContenidosMensual, costosGestionData, monthsToShow, calcularCostoSoftwareEstimado]
  );

  // ─── 11. YTDs ───
  const ytdMargenGestionContenidos = useMemo(
    () => margenGestionContenidosMensual.slice(0, monthsToShow).reduce((sum, val) => sum + val, 0),
    [margenGestionContenidosMensual, monthsToShow]
  );

  const ytdIngresosGestionContenidos = useMemo(
    () => ingresosGestionContenidosMensual.slice(0, monthsToShow).reduce((acc, val) => acc + val, 0),
    [ingresosGestionContenidosMensual, monthsToShow]
  );

  // ─── 12. gestionMensualesCombinados ───
  const gestionMensualesCombinados = useMemo(() => {
    if (!modoCombinado) return null;
    const resultado = Array(12).fill(0);
    for (let i = 0; i < 12; i++) {
      const gestionChile = ingresosGestionContenidosMensualChile[i] || 0;
      const gestionPeru = ingresosGestionContenidosMensualPeru[i] || 0;
      const chileUSD = gestionChile / (dolarMensualChile[i] || dolarFallback.Chile);
      const peruUSD = gestionPeru / (dolarMensualPeru[i] || dolarFallback["Perú"]);
      resultado[i] = chileUSD + peruUSD;
    }
    return resultado;
  }, [
    modoCombinado,
    ingresosGestionContenidosMensualChile,
    ingresosGestionContenidosMensualPeru,
    dolarMensualChile,
    dolarMensualPeru,
  ]);

  return {
    detalleGestionContenidosPorMes,
    calcularCostoSoftwareEstimado,
    getSoftwareDSValueA,
    getFechaFacturaHardware,
    ingresosGestionContenidosMensual,
    ingresosGestionContenidosMensualAnoAnterior,
    ingresosGestionContenidosMensualChile,
    ingresosGestionContenidosMensualPeru,
    gestionMensualesCombinados,
    ytdSoftwareDS,
    ytdOtros,
    margenGestionContenidosMensual,
    ytdMargenGestionContenidos,
    ytdIngresosGestionContenidos,
  };
}
